import api from '@shared/services/api';

export const identificatorsService = {
    getDeterms: async ({ searchQuery, pageNumber = 0, pageSize = 10, sortDir = 'asc' }) => {
        try {
            const params = {
                page: pageNumber,
                size: pageSize,
                sortOrder: sortDir,
                ...(searchQuery && { search: searchQuery })
            };

            const response = await api.get('/determs', { params });
            const { content, pageSize: responsePageSize, totalElements, pageNumber: responsePageNumber, totalPages } = response.data;

            if (!content) {
                console.error('Invalid response - no content:', response);
                throw new Error('Data not found');
            }

            return {
                content,
                totalPages,
                totalElements,
                pageNumber: responsePageNumber || pageNumber,
                pageSize: responsePageSize || pageSize
            };
        } catch (error) {
            console.error('Error fetching determs:', error);
            throw error;
        }
    },

    createDeterm: async (determData) => {
        try {
            const formattedData = {
                mnemonic: determData.code,
                typeDet: determData.typeOfIdentificator === '3', // true for Tree, false for Linear
                sizeDet: parseInt(determData.totalValue),
                note: determData.title,
                replication: true, // default value
                readOnly: false, // default value
                active: determData.active
            };

            const response = await api.post('/determs', formattedData);
            return response.data;
        } catch (error) {
            console.error('Error creating determ:', error);
            throw error;
        }
    },

    updateDeterm: async (id, determData) => {
        try {
            const formattedData = {
                mnemonic: determData.code,
                typeDet: determData.typeOfIdentificator === '3', // true for Tree, false for Linear
                sizeDet: parseInt(determData.totalValue),
                note: determData.title,
                replication: true, // default value
                readOnly: false, // default value
                active: determData.active
            };

            const response = await api.put(`/determs/${id}`, formattedData);
            return response.data;
        } catch (error) {
            console.error('Error updating determ:', error);
            throw error;
        }
    },

    deleteDeterm: async (id) => {
        try {
            const response = await api.delete(`/determs/${id}`);
            return response.data;
        } catch (error) {
            console.error('Error deleting determ:', error);
            throw error;
        }
    },

    getNomstruct: async (determId) => {
        try {
            const response = await api.get(`/nomstruct/${determId}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching nomstruct:', error);
            throw error;
        }
    },

    createNomstruct: async (nomstructData) => {
        try {
            const response = await api.post('/nomstruct', nomstructData);
            return response.data;
        } catch (error) {
            console.error('Error creating nomstruct:', error);
            throw error;
        }
    },

    updateNomstruct: async (id, nomstructData) => {
        try {
            const response = await api.put(`/nomstruct/${id}`, nomstructData);
            return response.data;
        } catch (error) {
            console.error('Error updating nomstruct:', error);
            throw error;
        }
    },

    deleteNomstruct: async (id) => {
        try {
            const response = await api.delete(`/nomstruct/${id}`);
            return response.data;
        } catch (error) {
            console.error('Error deleting nomstruct:', error);
            throw error;
        }
    },

    getNomenclatures: async (determId, parentValue = null) => {
        try {
            const params = {};
            if (parentValue != null && parentValue !== '') {
                const normalized = String(parentValue).trim();
                // Backend expects unpadded numeric parent values: "01" -> "1", "02" -> "2"
                params.parentValue = /^\d+$/.test(normalized)
                    ? String(Number(normalized))
                    : normalized;
            }
            const response = await api.get(`/nomenclatures/${determId}`, { params });
            return response.data;
        } catch (error) {
            console.error('Error fetching nomenclatures:', error);
            throw error;
        }
    },

    getTypeOfInsurance: async () => {
        try {
            const response = await api.get('/nomenclatures/4');
            return response.data;
        } catch (error) {
            console.error('Error fetching type of insurance:', error);
            throw error;
        }
    },


    createNomenclature: async (nomenclatureData) => {
        try {
            const response = await api.post('/nomenclatures', nomenclatureData);
            return response.data;
        } catch (error) {
            console.error('Error creating nomenclature:', error);
            throw error;
        }
    },

    updateNomenclature: async (id, nomenclatureData) => {
        try {
            const response = await api.put(`/nomenclatures/${id}`, nomenclatureData);
            return response.data;
        } catch (error) {
            console.error('Error updating nomenclature:', error);
            throw error;
        }
    },

    deleteNomenclature: async (id) => {
        try {
            const response = await api.delete(`/nomenclatures/${id}`);
            return response.data;
        } catch (error) {
            console.error('Error deleting nomenclature:', error);
            throw error;
        }
    },
    // New methods for report data (not PDF blob)
    getDetermsReportData: async (params = {}) => {
        const queryParams = new URLSearchParams();
        
        // Handle search query
        if (params.searchQuery) {
            queryParams.append('search', params.searchQuery);
        }
        
        const queryString = queryParams.toString();
        const url = `/determs/report${queryString ? `?${queryString}` : ''}`;
        const response = await api.get(url);
        return response.data || [];
    },

    getNomenclaturesReportData: async (id) => {
        const response = await api.get(`/nomenclatures/${id}/report`);
        return response.data || [];
    },

    getNomenclatureTranslations: async (id, { searchQuery, pageNumber = 0, pageSize = 10, sortDir = 'asc', value, transValue, note, transNote } = {}) => {
        try {
            const params = {
                page: pageNumber,
                size: pageSize,
                sortOrder: sortDir,
                ...(searchQuery && { search: searchQuery }),
                ...(value && { value: value }),
                ...(transValue && { transValue: transValue }),
                ...(note && { note: note }),
                ...(transNote && { transNote: transNote })
            };

            const response = await api.get(`/nomenclatures-trl/${id}`, { params });
            const { content, pageSize: responsePageSize, totalElements, pageNumber: responsePageNumber, totalPages } = response.data;

            if (!content) {
                console.error('Invalid response - no content:', response);
                throw new Error('Data not found');
            }

            return {
                content,
                totalPages,
                totalElements,
                pageNumber: responsePageNumber || pageNumber,
                pageSize: responsePageSize || pageSize
            };
        } catch (error) {
            console.error('Error fetching nomenclature translations:', error);
            throw error;
        }
    },

    createNomenclatureTranslation: async (translationData) => {
        try {
            const response = await api.post('/nomenclatures-trl', translationData);
            return response.data;
        } catch (error) {
            console.error('Error creating nomenclature translation:', error);
            throw error;
        }
    }
};

